'use client';

import React from 'react';
import { X, TrendingUp, TrendingDown } from 'lucide-react';
import type { PaperPosition, PaperPortfolio, PaperTrade } from '../utils/paperTradingStorage';
import { calcPositionPnL, savePortfolio } from '../utils/paperTradingStorage';

interface OpenPositionsTableProps {
  portfolio: PaperPortfolio;
  onPortfolioChange: (p: PaperPortfolio) => void;
  priceTick: number;
}

// Simulate price per symbol
function getSimPrice(pos: PaperPosition, tick: number): number {
  const noise = (Math.sin((tick + pos.entryPrice) * 0.1) * 0.003 + Math.cos((tick + pos.entryPrice) * 0.07) * 0.002) * pos.entryPrice;
  return Math.max(pos.entryPrice * 0.5, pos.entryPrice + noise);
}

export default function OpenPositionsTable({
  portfolio,
  onPortfolioChange,
  priceTick,
}: OpenPositionsTableProps) {
  const handleClose = (posId: string, exitReason: PaperTrade['exitReason'] = 'manual') => {
    const pos = portfolio.positions.find((p) => p.id === posId);
    if (!pos) return;

    const currentPrice = getSimPrice(pos, priceTick);
    const { pnl, pnlPct } = calcPositionPnL({ ...pos, currentPrice });
    const positionValue = pos.entryPrice * pos.quantity;

    const closedTrade: PaperTrade = {
      id: `trade-${Date.now()}`,
      symbol: pos.symbol,
      direction: pos.direction,
      entryPrice: pos.entryPrice,
      exitPrice: currentPrice,
      quantity: pos.quantity,
      pnl,
      pnlPct,
      openedAt: pos.openedAt,
      closedAt: new Date().toISOString(),
      strategy: pos.strategy,
      exitReason,
    };

    const updated: PaperPortfolio = {
      ...portfolio,
      virtualCash: portfolio.virtualCash + positionValue + pnl,
      positions: portfolio.positions.filter((p) => p.id !== posId),
      tradeHistory: [closedTrade, ...portfolio.tradeHistory],
    };

    savePortfolio(updated);
    onPortfolioChange(updated);
  };

  if (portfolio.positions.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6">
        <h2 className="text-sm font-700 text-foreground mb-4">Open Positions</h2>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
            <TrendingUp size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">No open positions</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Execute a trade to see it here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-700 text-foreground">Open Positions</h2>
        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-600">
          {portfolio.positions.length} active
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Symbol', 'Dir', 'Qty', 'Entry', 'Current', 'P&L', 'P&L %', 'Strategy', ''].map((h) => (
                <th key={h} className="text-left text-muted-foreground font-500 pb-2.5 pr-3 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {portfolio.positions.map((pos) => {
              const currentPrice = getSimPrice(pos, priceTick);
              const { pnl, pnlPct } = calcPositionPnL({ ...pos, currentPrice });
              const isProfit = pnl >= 0;

              return (
                <tr key={pos.id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-2.5 pr-3 font-700 text-foreground">{pos.symbol}</td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`flex items-center gap-1 font-600 ${
                        pos.direction === 'long' ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {pos.direction === 'long' ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {pos.direction === 'long' ? 'L' : 'S'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">{pos.quantity}</td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">${pos.entryPrice.toFixed(2)}</td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">${currentPrice.toFixed(2)}</td>
                  <td className={`py-2.5 pr-3 font-600 tabular-nums ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}${pnl.toFixed(2)}
                  </td>
                  <td className={`py-2.5 pr-3 font-600 tabular-nums ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}{pnlPct.toFixed(2)}%
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground max-w-[120px] truncate">{pos.strategy}</td>
                  <td className="py-2.5">
                    <button
                      onClick={() => handleClose(pos.id)}
                      className="w-6 h-6 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center transition-colors"
                      title="Close position"
                    >
                      <X size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
